import React, { useEffect, useState } from "react";
import photo1 from "../../Assets/Images/landing/hero-a.jpg";
import photo2 from "../../Assets/Images/landing/hero-b.jpg";
import photo3 from "../../Assets/Images/landing/hero-c.jpg";
import photo4 from "../../Assets/Images/landing/hero-d.jpg";
import photo5 from "../../Assets/Images/landing/hero-a.jpg";
import photo6 from "../../Assets/Images/landing/hero-b.jpg";

type VisualKind = "single" | "batch" | "templates" | "metadata" | "sync";

const items = [
  {
    tag: "Watermark Image",
    title: "Add watermark to your photos",
    body: "With eZy watermark, you can add watermarks to your photos, ensuring their security and enhancing their professional appearance — whether you're a pro photographer or an enthusiastic hobbyist.",
    visual: "single" as VisualKind,
  },
  {
    tag: "Batch watermarking",
    title: "Watermark a batch of photos",
    body: "eZy watermark lets you apply watermarks to an entire batch of photos in just a few clicks. Watermark 100s of photos in one go, and reposition or resize individually during upload.",
    visual: "batch" as VisualKind,
  },
  {
    tag: "Template creation",
    title: "Create your own templates",
    body: "Customize your workflow by designing your own templates. Store watermarks and their positions, then apply them across multiple photos for perfectly consistent results.",
    visual: "templates" as VisualKind,
  },
  {
    tag: "Metadata preserved",
    title: "Your images stay intact",
    body: "EXIF, ICC, and color profiles are preserved on export. Your photos look identical — just protected with your brand.",
    visual: "metadata" as VisualKind,
  },
  {
    tag: "Template syncing",
    title: "Your templates, everywhere",
    body: "Sign in and your custom templates are saved to your account. Switch to another phone, tablet, or the web — your watermarks sync automatically so you can protect photos from any device.",
    visual: "sync" as VisualKind,
  },
];

const BATCH_TOTAL = 124;
// Matches .lr-fv__batch-progress-bar div's `lr-progress-fill` CSS animation (2.4s loop, reaches
// 100% width at the 80% mark) so the "x / 124" count ticks up in sync with the bar filling.
const BATCH_CYCLE_MS = 2400;
const BATCH_FILL_MS = BATCH_CYCLE_MS * 0.8;

const BatchProgressCount: React.FC = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let raf: number;
    const tick = (now: number) => {
      const elapsed = now % BATCH_CYCLE_MS;
      const progress = Math.min(elapsed / BATCH_FILL_MS, 1);
      setCount(Math.round(progress * BATCH_TOTAL));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <span className="lr-fv__batch-progress-count">
      {count} / {BATCH_TOTAL}
    </span>
  );
};

// Cycles the "active" template tab A -> B -> C on its own, matching the batch card's
// always-moving feel instead of a permanently-static "B" highlight.
const TEMPLATE_TAB_CYCLE_MS = 1800;
const useActiveTemplateTab = () => {
  const [index, setIndex] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % 3), TEMPLATE_TAB_CYCLE_MS);
    return () => clearInterval(id);
  }, []);
  return index;
};

const TemplatesVisual: React.FC = () => {
  const activeTab = useActiveTemplateTab();
  return (
    <div className="lr-fv lr-fv--templates">
      <div className="lr-fv__stack-inner">
        <div className="lr-fv__stack-card lr-fv__stack-card--1">
          <img src={photo5} alt="" />
          <span style={{ bottom: 8, left: 8 }}>© STUDIO</span>
        </div>
        <div className="lr-fv__stack-card lr-fv__stack-card--2">
          <img src={photo2} alt="" />
          <span style={{ top: 8, right: 8 }}>© eZy</span>
        </div>
        <div className="lr-fv__stack-card lr-fv__stack-card--3">
          <img src={photo4} alt="" />
          <span style={{ bottom: 8, left: "50%", transform: "translateX(-50%)" }}>
            © PORTFOLIO 2026
          </span>
        </div>
      </div>
      <div className="lr-fv__stack-tabs">
        {["A", "B", "C"].map((t, i) => (
          <span key={t} className={i === activeTab ? "active" : ""}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

const SyncVisual: React.FC = () => (
  <div className="lr-fv lr-fv--sync">
    <div className="lr-fv__sync-devices">
      <div className="lr-fv__sync-device lr-fv__sync-device--phone">
        <img src={photo2} alt="" />
        <span>iPhone</span>
      </div>
      <div className="lr-fv__sync-hub">
        <span className="lr-fv__sync-hub-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
            <path d="M16 21h5v-5" />
          </svg>
        </span>
        <span className="lr-fv__sync-hub-label">Auto-sync</span>
      </div>
      <div className="lr-fv__sync-device lr-fv__sync-device--tablet">
        <img src={photo4} alt="" />
        <span>iPad</span>
      </div>
    </div>
    <div className="lr-fv__sync-beam" />
    <div className="lr-fv__sync-card">
      <span />
      <span />
      <span />
    </div>
  </div>
);

const FeatureVisual: React.FC<{ kind: VisualKind }> = ({ kind }) => {
  if (kind === "single") {
    return (
      <div className="lr-fv lr-fv--single">
        <img src={photo3} alt="" />
        <div className="lr-fv__scrim" />
        <div className="lr-fv__tile" />
        <div className="lr-fv__scan" />
        <div className="lr-fv__protected">
          <span />
          PROTECTED
        </div>
        <div className="lr-fv__watermark-badge">© eZy Watermark</div>
      </div>
    );
  }
  if (kind === "batch") {
    const shots = [photo1, photo2, photo4, photo5, photo6, photo3];
    return (
      <div className="lr-fv lr-fv--batch">
        <div className="lr-fv__batch-grid">
          {shots.map((s, i) => (
            <div key={i} className="lr-fv__batch-thumb">
              <img src={s} alt="" />
              <div className="lr-fv__batch-thumb-stamp">
                <span>© eZy</span>
              </div>
              <div className="lr-fv__batch-thumb-check">✓</div>
            </div>
          ))}
        </div>
        <div className="lr-fv__batch-progress">
          <span>Batch progress</span>
          <div className="lr-fv__batch-progress-bar">
            <div />
          </div>
          <BatchProgressCount />
        </div>
      </div>
    );
  }
  if (kind === "templates") {
    return <TemplatesVisual />;
  }
  if (kind === "sync") {
    return <SyncVisual />;
  }
  // metadata
  return (
    <div className="lr-fv">
      <img src={photo1} alt="" />
      <span className="lr-fv__chip" style={{ top: 12, left: 12, animationDelay: "0s" }}>ƒ/2.8</span>
      <span className="lr-fv__chip" style={{ top: 12, right: 12, animationDelay: "0.6s" }}>1/250s</span>
      <span className="lr-fv__chip" style={{ bottom: 56, left: 12, animationDelay: "1.2s" }}>ISO 400</span>
      <span className="lr-fv__chip" style={{ bottom: 56, right: 12, animationDelay: "1.8s" }}>24mm</span>
      <span
        className="lr-fv__chip"
        style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "rgba(243,101,115,0.95)", color: "#fff", animationDelay: "2.4s" }}
      >
        EXIF · ICC preserved
      </span>
      <div className="lr-fv__color-profile">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f36573" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <strong>Color profile</strong>
        <span>sRGB · Adobe RGB · ProPhoto</span>
      </div>
    </div>
  );
};

/**
 * Purely visual showcase for the landing page. Distinct from the shared
 * Components/LandingComponents/WaterMarkFeature.tsx, which is also used by the /features page
 * and is left untouched.
 */
const FeaturesShowcase: React.FC = () => {
  return (
    <section id="features" className="lr-section">
      <div className="lr-section__inner lr-features">
        <div className="lr-features__head">
          <div className="lr-features__head-copy">
            <span className="lr-section__eyebrow">Features</span>
            <h2 className="lr-section__heading">
              Everything you need to watermark like a pro.
            </h2>
          </div>
          <p className="lr-features__lede">
            Built for photographers, real-estate agents, artists, and shops — anyone who needs
            to protect a lot of images fast.
          </p>
        </div>
        <div className="lr-features__grid">
          {items.map((it) => (
            <div key={it.title} className="lr-features__card">
              <div className="lr-features__glow" />
              <FeatureVisual kind={it.visual} />
              <div>
                <span className="lr-features__tag">{it.tag}</span>
                <h3 className="lr-features__title">{it.title}</h3>
                <p className="lr-features__body">{it.body}</p>
                <button type="button" className="lr-features__link">
                  Read More →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesShowcase;
